// The column-mapping panel: a salesperson maps the buyer's spreadsheet /
// BOQ columns to description / quantity / unit, against the raw rows shown
// beside the pickers, then confirms — the worker creates the line items.
//
// The grid is re-fetched on every picker change (useUploadGrid with the
// current candidate) so the live preview never drifts from what confirming
// will actually save. Hand-rolled table — no data-grid library (admin/
// CLAUDE.md).

import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useUploadGrid, useConfirmUploadMapping } from '../hooks/useRequestUploads.js';
import LoadingState from './LoadingState.jsx';
import ErrorState from './ErrorState.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './UploadMappingPanel.module.css';

const HEADER_ROW_CHOICES = Array.from({ length: 15 }, (_, i) => i + 1);

const SKIP_KEYS = {
  blank_row: 'workspace.mapping.skip.blankRow',
  missing_description: 'workspace.mapping.skip.missingDescription',
  description_too_long: 'workspace.mapping.skip.descriptionTooLong',
  missing_or_invalid_quantity: 'workspace.mapping.skip.missingQuantity',
};

const CANDIDATE_KEYS = [
  'headerRow',
  'descriptionColumn',
  'descriptionColumn2',
  'quantityColumn',
  'unitColumn',
  'skuColumn',
];

export default function UploadMappingPanel({ requestId, upload }) {
  const { t } = useLanguage();

  // The salesperson's explicit picks. A key is present only once touched;
  // its value is '' for "none" (an optional column cleared) or a 1-based
  // index / row number as a string.
  const [picks, setPicks] = useState({});
  const [phase, setPhase] = useState(() =>
    upload.parseStatus === 'mapped' ? 'creating' : 'editing'
  );

  // The worker bounced the confirmed mapping back (it matched zero rows) or
  // finished — react to the upload prop the parent re-reads while polling.
  useEffect(() => {
    if (upload.parseStatus === 'mapped') {
      setPhase('creating');
    } else if (upload.parseStatus === 'rows_extracted') {
      setPhase('editing');
    }
  }, [upload.parseStatus]);

  const candidate = useMemo(() => {
    const next = {};
    CANDIDATE_KEYS.forEach((key) => {
      if (key in picks && picks[key] !== '') next[key] = Number(picks[key]);
    });
    return next;
  }, [picks]);

  const gridQuery = useUploadGrid(requestId, upload.id, candidate, {
    enabled: phase === 'editing',
  });
  const grid = gridQuery.data;
  const confirmMutation = useConfirmUploadMapping(requestId, upload.id);
  const { toastOk, toastErr } = useActionFeedback();

  const proposed = grid?.proposedMapping || {};
  const value = (key, fallback) => {
    if (key in picks) return picks[key];
    return fallback != null ? String(fallback) : '';
  };

  const headerRowValue = value('headerRow', grid?.headerRow);
  const descriptionValue = value('descriptionColumn', proposed.descriptionColumn);
  const description2Value = value('descriptionColumn2', proposed.descriptionColumn2);
  const quantityValue = value('quantityColumn', proposed.quantityColumn);
  const unitValue = value('unitColumn', proposed.unitColumn);
  const skuValue = value('skuColumn', proposed.skuColumn);

  const setPick = (key, next) => {
    setPicks((prev) => ({ ...prev, [key]: next }));
    confirmMutation.reset();
  };

  const canConfirm = Boolean(headerRowValue) && Boolean(descriptionValue) && Boolean(quantityValue);

  const lineCount = grid?.rows ? grid.rows.filter((r) => r.parsed).length : 0;

  const onConfirm = () => {
    if (!canConfirm) return;
    const body = {
      headerRow: Number(headerRowValue),
      descriptionColumn: Number(descriptionValue),
      quantityColumn: Number(quantityValue),
    };
    if (description2Value) body.descriptionColumn2 = Number(description2Value);
    if (unitValue) body.unitColumn = Number(unitValue);
    if (skuValue) body.skuColumn = Number(skuValue);

    confirmMutation.mutate(body, {
      onSuccess: () => {
        setPhase('creating');
        toastOk(t('workspace.mapping.confirmedToast'));
      },
      onError: (err) => toastErr(err, ['UPLOAD_NOT_READY', 'UPLOAD_ALREADY_PROCESSED']),
    });
  };

  // --- Worker is creating the line items --------------------------------
  if (phase === 'creating') {
    return (
      <section className={styles.panel} aria-labelledby="mapping-panel-title">
        <h2 id="mapping-panel-title" className={styles.title}>
          {t('workspace.mapping.title')}
        </h2>
        <p className={styles.creating} role="status" aria-live="polite">
          {t('workspace.mapping.creating')}
        </p>
      </section>
    );
  }

  const bounceHint =
    upload.parseStatus === 'rows_extracted' && upload.parseError
      ? t('workspace.mapping.zeroRowsHint')
      : null;

  const columnOptions = (grid?.columns || []).map((col) => (
    <option key={col.index} value={col.index}>
      {col.headerText
        ? t('workspace.mapping.columnOption', { letter: col.letter, headerText: col.headerText })
        : t('workspace.mapping.columnOptionBlank', { letter: col.letter })}
    </option>
  ));

  return (
    <section className={styles.panel} aria-labelledby="mapping-panel-title">
      <h2 id="mapping-panel-title" className={styles.title}>
        {t('workspace.mapping.title')}
      </h2>
      <p className={styles.intro}>{t('workspace.mapping.intro')}</p>

      <div className={styles.body}>
        <div className={styles.rawSide}>
          {gridQuery.isLoading && <LoadingState variant="rows" rows={8} />}

          {gridQuery.isError && (
            <ErrorState
              messageId="workspace.mapping.gridError"
              error={gridQuery.error}
              onRetry={gridQuery.refetch}
            />
          )}

          {grid && grid.parseStatus === 'failed' && (
            <p className={styles.failed}>{t('workspace.mapping.fileUnreadable')}</p>
          )}

          {grid && grid.parseStatus !== 'failed' && grid.rows.length > 0 && (
            <div className={styles.tableScroll}>
              <table className={styles.rawTable}>
                <thead>
                  <tr>
                    <th className={styles.rowNumCol}>{t('workspace.mapping.col.row')}</th>
                    {grid.columns.map((col) => (
                      <th key={col.index}>
                        <span className={styles.colLetter}>{col.letter}</span>
                        {col.headerText ? ` ${col.headerText}` : ''}
                      </th>
                    ))}
                    <th className={styles.becomesCol}>{t('workspace.mapping.col.becomes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {grid.rows.map((row) => (
                    <tr key={row.rowNumber} className={row.isHeader ? styles.headerRow : undefined}>
                      <td className={`${styles.rowNumCol} tabularNums`}>{row.rowNumber}</td>
                      {row.cells.map((cell, i) => (
                        <td key={i}>{cell}</td>
                      ))}
                      <td className={styles.becomesCol}>
                        {row.parsed ? (
                          <span className={styles.becomesText}>
                            {row.parsed.rawUnit
                              ? t('workspace.mapping.becomes', {
                                  qty: row.parsed.qty,
                                  unit: row.parsed.rawUnit,
                                  description: row.parsed.rawDescription,
                                })
                              : t('workspace.mapping.becomesNoUnit', {
                                  qty: row.parsed.qty,
                                  description: row.parsed.rawDescription,
                                })}
                          </span>
                        ) : row.isDataRow && row.skipReason ? (
                          <span className={styles.skipReason}>
                            {t(
                              SKIP_KEYS[row.skipReason] || 'workspace.mapping.skip.missingQuantity'
                            )}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={styles.controlSide}>
          {bounceHint && <p className={styles.bounceHint}>{bounceHint}</p>}

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('workspace.mapping.headerRowLabel')}</span>
            <select
              className={styles.select}
              value={headerRowValue}
              onChange={(e) => setPick('headerRow', e.target.value)}
            >
              <option value="">{t('workspace.mapping.headerRowNone')}</option>
              {HEADER_ROW_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('workspace.mapping.descriptionLabel')}</span>
            <select
              className={styles.select}
              value={descriptionValue}
              onChange={(e) => setPick('descriptionColumn', e.target.value)}
              disabled={!grid || grid.columns.length === 0}
            >
              <option value="">{t('workspace.mapping.columnNone')}</option>
              {columnOptions}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('workspace.mapping.description2Label')}</span>
            <span className={styles.fieldHint}>{t('workspace.mapping.description2Hint')}</span>
            <select
              className={styles.select}
              value={description2Value}
              onChange={(e) => setPick('descriptionColumn2', e.target.value)}
              disabled={!grid || grid.columns.length === 0}
            >
              <option value="">{t('workspace.mapping.columnNone')}</option>
              {columnOptions}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('workspace.mapping.quantityLabel')}</span>
            <select
              className={styles.select}
              value={quantityValue}
              onChange={(e) => setPick('quantityColumn', e.target.value)}
              disabled={!grid || grid.columns.length === 0}
            >
              <option value="">{t('workspace.mapping.columnNone')}</option>
              {columnOptions}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('workspace.mapping.unitLabel')}</span>
            <select
              className={styles.select}
              value={unitValue}
              onChange={(e) => setPick('unitColumn', e.target.value)}
              disabled={!grid || grid.columns.length === 0}
            >
              <option value="">{t('workspace.mapping.columnNone')}</option>
              {columnOptions}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('workspace.mapping.skuLabel')}</span>
            <span className={styles.fieldHint}>{t('workspace.mapping.skuHint')}</span>
            <select
              className={styles.select}
              value={skuValue}
              onChange={(e) => setPick('skuColumn', e.target.value)}
              disabled={!grid || grid.columns.length === 0}
            >
              <option value="">{t('workspace.mapping.columnNone')}</option>
              {columnOptions}
            </select>
          </label>

          <button
            type="button"
            className={styles.confirmButton}
            onClick={onConfirm}
            disabled={!canConfirm || confirmMutation.isPending}
          >
            {confirmMutation.isPending && <Spinner />}{' '}
            {confirmMutation.isPending
              ? t('workspace.mapping.confirming')
              : t('workspace.mapping.confirm', { count: lineCount })}
          </button>
        </div>
      </div>
    </section>
  );
}
